import React from 'react';
import { Box, Button, SxProps, Theme, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ASSESSMENT_URL } from '../../../Utils/urls';

export interface FourthSectionProps {
	title?: React.ReactNode;
	description?: React.ReactNode;
	buttonLabel?: string;
	buttonLink?: string;
	sx?: SxProps<Theme>;
}

const FourthSection = ({
	title = (
		<>
			Decide who moves forward
			<br />
			on evidence.
		</>
	),
	description = 'Turn your interview conversations into defensible scorecards. Start today or book a walkthrough.',
	buttonLabel = 'Start a Free Trial',
	buttonLink = ASSESSMENT_URL,
	sx
}: FourthSectionProps) => {
	const navigate = useNavigate();

	const handleClick = () => {
		if (!buttonLink) return;
		if (/^https?:\/\//i.test(buttonLink)) {
			window.open(buttonLink, '_blank', 'noopener,noreferrer');
			return;
		}

		navigate(buttonLink);
	};

	return (
		<Box
			component="section"
			sx={[
				{
					position: 'relative',
					display: 'flex',
					minHeight: { xs: 360, sm: 410, md: 485 },
					alignItems: 'center',
					justifyContent: 'center',
					overflow: 'hidden',
					backgroundColor: '#292929',
					backgroundImage: [
						'linear-gradient(rgba(255, 255, 255, 0.055) 1px, transparent 1px)',
						'linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px)',
						'radial-gradient(ellipse 52% 88% at 50% 48%, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.045) 43%, transparent 76%)',
						'radial-gradient(circle at 8% 100%, rgba(255, 255, 255, 0.055), transparent 34%)',
						'linear-gradient(90deg, #292929 0%, #252525 48%, #292929 100%)'
					].join(', '),
					backgroundPosition: 'center',
					backgroundSize: '68px 68px, 68px 68px, auto, auto, auto',
					px: { xs: 2.5, sm: 4 },
					py: { xs: 8, sm: 10, md: 12 }
				},
				...(Array.isArray(sx) ? sx : [sx])
			]}
		>
			<Box
				sx={{
					position: 'relative',
					zIndex: 1,
					width: '100%',
					maxWidth: 980,
					mx: 'auto',
					textAlign: 'center'
				}}
			>
				<Typography
					component="h2"
					sx={{
						m: 0,
						color: '#ffffff',
						fontFamily: 'DM Sans, sans-serif',
						fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
						fontWeight: 600,
						lineHeight: { xs: 1.12, md: 1.08 },
						letterSpacing: '-0.035em'
					}}
				>
					{title}
				</Typography>

				{description && (
					<Typography
						component="div"
						sx={{
							maxWidth: 880,
							mt: { xs: 2.5, md: 3 },
							mx: 'auto',
							color: 'rgba(255, 255, 255, 0.88)',
							fontFamily: 'DM Mono, monospace',
							fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
							fontWeight: 400,
							lineHeight: 1.55,
							letterSpacing: '-0.015em'
						}}
					>
						{description}
					</Typography>
				)}

				<Button
					variant="contained"
					onClick={handleClick}
					sx={{
						mt: { xs: 4, md: 5 },
						px: 2.25,
						py: 1.25,
						border: '4px solid rgba(215, 236, 255, 0.28)',
						borderRadius: '8px',
						backgroundColor: '#ffffff',
						boxShadow: 'none',
						color: '#0073de',
						fontFamily: 'DM Mono, monospace',
						fontSize: { xs: '0.875rem', md: '1rem' },
						fontWeight: 500,
						lineHeight: 1.5,
						letterSpacing: '-0.02em',
						textTransform: 'none',
						whiteSpace: 'nowrap',
						'&:hover': {
							borderColor: '#ffffff',
							backgroundColor: '#0073de',
							color: '#ffffff',
							boxShadow: 'none'
						},
						'&:focus-visible': {
							outline: '2px solid #ffffff',
							outlineOffset: 3
						}
					}}
				>
					{buttonLabel}
				</Button>
			</Box>
		</Box>
	);
};

export default FourthSection;
